/**
 * Alpine.js component for sibling color picker
 * Handles dynamic product switching without page reload
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('siblingColorPicker', (currentHandle, sectionId) => ({
    // State
    currentProductHandle: currentHandle,
    currentProductTitle: '',
    loadingHandle: null,
    sectionId: sectionId,

    // Initialize
    init() {
      this.currentProductTitle = this.getCurrentProductTitle();
    },

    // Get current product title from page
    getCurrentProductTitle() {
      const titleElement = document.querySelector('.product-single__title');
      return titleElement ? titleElement.textContent.trim() : '';
    },

    // Load sibling product dynamically
    async loadSiblingProduct(siblingHandle, siblingUrl) {
      // Prevent multiple simultaneous loads
      if (this.loadingHandle) return;

      this.loadingHandle = siblingHandle;

      try {
        // Fetch product JSON
        const response = await fetch(`${siblingUrl}.js`);
        if (!response.ok) throw new Error('Failed to fetch product');

        const productData = await response.json();

        // Update the page
        await this.updateProductPage(productData);

        // Find first available variant
        const firstAvailableVariant = productData.variants.find(v => v.available) || productData.variants[0];

        // Update URL without reload, including variant parameter
        const newUrl = new URL(siblingUrl, window.location.origin);
        if (firstAvailableVariant) {
          newUrl.searchParams.set('variant', firstAvailableVariant.id);
        }
        window.history.pushState({
          productHandle: siblingHandle,
          variantId: firstAvailableVariant?.id
        }, '', newUrl);

        // Update current state
        this.currentProductHandle = siblingHandle;
        this.currentProductTitle = productData.title;

        // Dispatch custom event for other components
        document.dispatchEvent(new CustomEvent('sibling-product:changed', {
          detail: { product: productData, url: siblingUrl }
        }));

      } catch (error) {
        console.error('Error loading sibling product:', error);
        // Fallback to page navigation
        window.location.href = siblingUrl;
      } finally {
        this.loadingHandle = null;
      }
    },

    // Update all product elements on the page
    async updateProductPage(productData) {
      // Find the first available variant (not just variants[0])
      const selectedVariant = productData.variants.find(v => v.available) || productData.variants[0];

      // Update product title
      this.updateProductTitle(productData.title);

      // Update product images/gallery
      this.updateProductImages(productData);

      // Update variant options (if any) - THIS MUST HAPPEN BEFORE OTHER UPDATES
      this.updateVariantOptions(productData);

      // Update no-js fallback select
      this.updateNoJsSelect(productData, selectedVariant);

      // Update product form with selected variant
      this.updateProductForm(productData, selectedVariant);

      // Update product price based on currently selected options
      this.updateProductPrice(selectedVariant);

      // Update product description
      this.updateProductDescription(productData);

      // Update SKU if displayed
      this.updateProductSku(selectedVariant);

      // Update availability/inventory
      this.updateProductAvailability(selectedVariant);

      // Update variant JSON data
      this.updateVariantData(productData, selectedVariant);

      // Reinitialize product scripts if needed
      this.reinitializeProductScripts(productData);
    },

    updateProductTitle(title) {
      const titleElement = document.querySelector('.product-single__title');
      if (titleElement) {
        titleElement.textContent = title;
      }
    },

    updateProductPrice(variant) {
      const priceElement = document.querySelector('[data-product-price]');
      if (priceElement && variant.price) {
        const formattedPrice = this.formatMoney(variant.price);
        priceElement.textContent = formattedPrice;

        // Update compare at price if exists
        const compareElement = document.querySelector('[data-compare-price]');
        if (compareElement) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareElement.textContent = this.formatMoney(variant.compare_at_price);
            compareElement.closest('[data-product-price-wrap]')?.classList.remove('hide');
            priceElement.classList.add('on-sale');
          } else {
            compareElement.closest('[data-product-price-wrap]')?.classList.add('hide');
            priceElement.classList.remove('on-sale');
          }
        }

        // Update save price
        this.updateSavePrice(variant);
      }
    },

    updateSavePrice(variant) {
      const saveElement = document.querySelector('[data-save-price]');
      if (saveElement && variant.compare_at_price && variant.compare_at_price > variant.price) {
        const savings = variant.compare_at_price - variant.price;
        const formattedSavings = this.formatMoney(savings);
        saveElement.innerHTML = `Save ${formattedSavings}`;
        saveElement.classList.remove('hide');
      } else if (saveElement) {
        saveElement.classList.add('hide');
      }
    },

    updateProductImages(productData) {
      // For Impulse theme, rebuild entire gallery HTML
      const productSection = document.querySelector('[data-section-type="product"]');
      const mainPhotosContainer = document.querySelector('[data-product-photos]');
      const thumbsContainer = document.querySelector('[data-product-thumbs]');

      if (!mainPhotosContainer || !productData.media || productData.media.length === 0) {
        return;
      }

      const images = productData.media.filter(m => m.media_type === 'image');
      if (images.length === 0) return;

      // Rebuild main slides
      mainPhotosContainer.innerHTML = '';
      images.forEach((image, index) => {
        const slide = document.createElement('div');
        slide.className = 'product-main-slide starting-slide';
        slide.setAttribute('data-index', index);

        slide.innerHTML = `
          <a href="${image.src}"
             data-product-single-media-wrapper
             data-enable-zoom="true"
             data-image-id="${image.id}">
            <div class="image-wrap" style="height: 0; padding-bottom: ${100 / (image.aspect_ratio || 1)}%;">
              <img src="${this.getImageUrl(image.src, '620x')}"
                   srcset="${this.generateSrcset(image.src)}"
                   alt="${image.alt || productData.title}"
                   class="product-main-slide__image image-element"
                   data-photoswipe-src="${image.src}"
                   data-photoswipe-width="${image.width || 2048}"
                   data-photoswipe-height="${image.height || 2048}"
                   loading="eager"
                   style="opacity: 1 !important;">
            </div>
          </a>
        `;
        mainPhotosContainer.appendChild(slide);
      });

      // Rebuild thumbnails
      if (thumbsContainer && images.length > 1) {
        const thumbsScroller = thumbsContainer.querySelector('.product__thumbs--scroller');
        if (thumbsScroller) {
          thumbsScroller.innerHTML = '';

          images.forEach((image, index) => {
            const thumbItem = document.createElement('div');
            thumbItem.className = 'product__thumb-item';
            thumbItem.setAttribute('data-index', index);
            thumbItem.style.display = 'block';

            thumbItem.innerHTML = `
              <a href="${this.getImageUrl(image.src, '1024x1024')}"
                 data-product-thumb
                 class="product__thumb"
                 data-index="${index}"
                 data-id="${image.id}"
                 style="display: block;">
                <div class="image-wrap image-wrap__thumbnail" style="height: 0; padding-bottom: 100%;">
                  <img src="${this.getImageUrl(image.src, '160x')}"
                       alt="${image.alt || productData.title}"
                       class="image-element lazyloaded"
                       style="display: block; opacity: 1 !important;">
                </div>
              </a>
            `;
            thumbsScroller.appendChild(thumbItem);
          });
        }

        // Make sure thumbs container is visible
        thumbsContainer.style.display = 'block';
        thumbsContainer.classList.remove('medium-up--hide', 'hide');
      }

      // Trigger theme reinitialization
      this.reinitializeProductGallery(productSection);
    },

    reinitializeProductGallery(productSection) {
      if (!productSection) return;

      const sectionId = productSection.dataset.sectionId;

      // Try to reinitialize using theme's section reload
      if (window.theme && window.Shopify && window.Shopify.designMode) {
        document.dispatchEvent(new CustomEvent('shopify:section:load', {
          detail: { sectionId: sectionId }
        }));
      }

      // Manual reinitialization for product thumbnails
      setTimeout(() => {
        const thumbnails = document.querySelectorAll('[data-product-thumb]');
        const mainSlides = document.querySelectorAll('.product-main-slide');

        thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', (e) => {
            e.preventDefault();

            // Hide all slides
            mainSlides.forEach(slide => {
              slide.style.display = 'none';
            });

            // Show selected slide
            if (mainSlides[index]) {
              mainSlides[index].style.display = 'block';
            }

            // Update active thumbnail
            thumbnails.forEach(t => {
              t.closest('.product__thumb-item').classList.remove('active-thumb');
            });
            thumb.closest('.product__thumb-item').classList.add('active-thumb');
          });
        });

        // Show first slide, hide others
        mainSlides.forEach((slide, index) => {
          slide.style.display = index === 0 ? 'block' : 'none';
        });

        // Make first thumbnail active
        if (thumbnails[0]) {
          thumbnails[0].closest('.product__thumb-item').classList.add('active-thumb');
        }
      }, 100);
    },

    updateVariantOptions(productData) {
      // Rebuild variant pickers for new product's options
      const productSection = document.querySelector('[data-section-type="product"]');
      if (!productSection) return;

      const sectionId = productSection.dataset.sectionId;
      const formId = `product-form-${sectionId}`;

      // Find all variant wrapper containers
      const variantWrappers = document.querySelectorAll('.variant-wrapper');

      if (variantWrappers.length === 0) return;

      // Store the original picker types before clearing
      const originalPickerTypes = Array.from(variantWrappers).map(wrapper => {
        return wrapper.getAttribute('data-type') || 'button';
      });

      // Get the parent container where variant pickers are located
      const variantPickerBlock = variantWrappers[0].closest('.product-block');

      if (!variantPickerBlock) return;

      // Clear existing variant pickers
      variantWrappers.forEach(wrapper => wrapper.remove());

      // Find first available variant to pre-select its options
      const firstAvailableVariant = productData.variants.find(v => v.available) || productData.variants[0];

      // Rebuild variant pickers based on new product's options
      if (productData.options && productData.options.length > 0) {
        productData.options.forEach((option, optionIndex) => {
          // Use the original picker type if available, otherwise default to button
          const pickerType = originalPickerTypes[optionIndex] || 'button';

          if (pickerType === 'button') {
            this.renderButtonVariantPicker(productData, option, optionIndex, formId, sectionId, variantPickerBlock, firstAvailableVariant);
          } else {
            this.renderDropdownVariantPicker(productData, option, optionIndex, formId, sectionId, variantPickerBlock, firstAvailableVariant);
          }
        });
      }

      // Reinitialize variant selection logic
      this.reinitializeVariantSelection(productData);
    },

    renderButtonVariantPicker(productData, option, optionIndex, formId, sectionId, container, firstAvailableVariant) {
      const wrapper = document.createElement('div');
      wrapper.className = 'variant-wrapper js';
      wrapper.setAttribute('data-type', 'button');

      // Create label
      const label = document.createElement('label');
      label.className = 'variant__label';
      label.setAttribute('for', `ProductSelect-${sectionId}-${productData.id}-option-${optionIndex}`);
      label.innerHTML = `${option.name} <span class="variant__label-info" data-variant-color-label data-index="${optionIndex}"></span>`;

      wrapper.appendChild(label);

      // Create fieldset for radio buttons
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'variant-input-wrap';
      fieldset.setAttribute('name', option.name);
      fieldset.setAttribute('data-index', `option${optionIndex + 1}`);
      fieldset.setAttribute('data-handle', this.handleize(option.name));
      fieldset.setAttribute('id', `ProductSelect-${sectionId}-${productData.id}-option-${optionIndex}`);

      const legend = document.createElement('legend');
      legend.className = 'hide';
      legend.textContent = option.name;
      fieldset.appendChild(legend);

      // Get the value that should be selected from first available variant
      const selectedValue = firstAvailableVariant ? firstAvailableVariant.options[optionIndex] : option.values[0];

      // Create radio button for each option value
      option.values.forEach((value) => {
        const isAvailable = this.isOptionValueAvailable(productData, optionIndex, value);

        const inputDiv = document.createElement('div');
        inputDiv.className = 'variant-input';
        inputDiv.setAttribute('data-index', `option${optionIndex + 1}`);
        inputDiv.setAttribute('data-value', value);

        const input = document.createElement('input');
        input.type = 'radio';
        input.setAttribute('form', formId);
        input.value = value;
        input.name = option.name;
        input.setAttribute('data-index', `option${optionIndex + 1}`);
        input.setAttribute('data-variant-input', '');
        input.id = `ProductSelect-${sectionId}-${productData.id}-option-${this.handleize(option.name)}-${encodeURIComponent(value)}`;

        // Check if this is the value from first available variant
        if (value === selectedValue) {
          input.checked = true;
        }

        if (!isAvailable) {
          input.classList.add('disabled');
        }

        const labelElement = document.createElement('label');
        labelElement.setAttribute('for', input.id);
        labelElement.className = `variant__button-label${!isAvailable ? ' disabled' : ''}`;
        labelElement.textContent = value;

        inputDiv.appendChild(input);
        inputDiv.appendChild(labelElement);
        fieldset.appendChild(inputDiv);
      });

      wrapper.appendChild(fieldset);
      container.appendChild(wrapper);
    },

    renderDropdownVariantPicker(productData, option, optionIndex, formId, sectionId, container, firstAvailableVariant) {
      const wrapper = document.createElement('div');
      wrapper.className = 'variant-wrapper variant-wrapper--dropdown js';
      wrapper.setAttribute('data-type', 'dropdown');

      // Create label
      const label = document.createElement('label');
      label.className = 'variant__label';
      label.setAttribute('for', `SingleOptionSelector-${sectionId}-${productData.id}-option-${optionIndex}`);
      label.textContent = option.name;

      wrapper.appendChild(label);

      // Create select wrapper
      const selectWrapper = document.createElement('div');
      selectWrapper.className = 'variant-input-wrap';
      selectWrapper.setAttribute('data-index', `option${optionIndex + 1}`);
      selectWrapper.setAttribute('data-handle', this.handleize(option.name));

      // Create select dropdown
      const select = document.createElement('select');
      select.setAttribute('form', formId);
      select.setAttribute('data-variant-input', '');
      select.id = `SingleOptionSelector-${sectionId}-${productData.id}-option-${optionIndex}`;
      select.setAttribute('data-index', `option${optionIndex + 1}`);

      // Get the value that should be selected from first available variant
      const selectedValue = firstAvailableVariant ? firstAvailableVariant.options[optionIndex] : option.values[0];

      option.values.forEach((value) => {
        const isAvailable = this.isOptionValueAvailable(productData, optionIndex, value);

        const optionElement = document.createElement('option');
        optionElement.value = value;
        optionElement.textContent = value;
        optionElement.name = option.name;
        optionElement.className = 'variant-input';
        optionElement.setAttribute('data-index', `option${optionIndex + 1}`);

        if (value === selectedValue) {
          optionElement.selected = true;
        }

        if (!isAvailable) {
          optionElement.disabled = true;
        }

        select.appendChild(optionElement);
      });

      selectWrapper.appendChild(select);
      wrapper.appendChild(selectWrapper);
      container.appendChild(wrapper);
    },

    isOptionValueAvailable(productData, optionIndex, optionValue) {
      // Check if any variant with this option value is available
      return productData.variants.some(variant => {
        return variant.options[optionIndex] === optionValue && variant.available;
      });
    },

    handleize(str) {
      return str.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    },

    reinitializeVariantSelection(productData) {
      // Store product data for later use
      this.currentProductData = productData;

      // Reinitialize theme's variant selection functionality
      const variantInputs = document.querySelectorAll('[data-variant-input]');

      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.handleVariantChange();
        });
      });

      // Also listen to the no-js select
      const noJsSelect = document.querySelector('select[name="id"][data-product-select]');
      if (noJsSelect) {
        noJsSelect.addEventListener('change', (e) => {
          const variantId = parseInt(e.target.value);
          const variant = productData.variants.find(v => v.id === variantId);
          if (variant) {
            this.updateProductPrice(variant);
            this.updateProductSku(variant);
            this.updateProductAvailability(variant);
          }
        });
      }
    },

    handleVariantChange() {
      // Get all selected option values
      const selectedOptions = [];
      const variantWrappers = document.querySelectorAll('.variant-wrapper');

      variantWrappers.forEach(wrapper => {
        const radioInput = wrapper.querySelector('input[type="radio"]:checked');
        const selectInput = wrapper.querySelector('select');

        if (radioInput) {
          selectedOptions.push(radioInput.value);
        } else if (selectInput) {
          selectedOptions.push(selectInput.value);
        }
      });

      if (!this.currentProductData) return;

      // Find matching variant
      const selectedVariant = this.currentProductData.variants.find(variant => {
        return variant.options.every((option, index) => {
          return option === selectedOptions[index];
        });
      });

      if (selectedVariant) {
        // Update no-js select to match
        const noJsSelect = document.querySelector('select[name="id"][data-product-select]');
        if (noJsSelect) {
          noJsSelect.value = selectedVariant.id;
        }

        // Update URL with variant parameter
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('variant', selectedVariant.id);
        window.history.replaceState({ variantId: selectedVariant.id }, '', currentUrl);

        // Update product details with new variant
        this.updateProductPrice(selectedVariant);
        this.updateProductSku(selectedVariant);
        this.updateProductAvailability(selectedVariant);
        this.updateProductForm(this.currentProductData, selectedVariant);
      }
    },

    updateProductForm(productData, selectedVariant) {
      // Update form variant ID
      const variantIdInput = document.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = selectedVariant.id;
      }

      // Update add to cart button
      const addToCartButton = document.querySelector('[data-add-to-cart]');
      if (addToCartButton) {
        const buttonText = addToCartButton.querySelector('[data-add-to-cart-text]');
        if (selectedVariant.available) {
          addToCartButton.disabled = false;
          if (buttonText) {
            const defaultText = buttonText.dataset.defaultText || 'Add to Cart';
            buttonText.textContent = defaultText;
          }
        } else {
          addToCartButton.disabled = true;
          if (buttonText) {
            buttonText.textContent = 'Sold Out';
          }
        }
      }
    },

    updateProductDescription(productData) {
      const descriptionElement = document.querySelector('.product-single__description');
      if (descriptionElement && productData.description) {
        descriptionElement.innerHTML = productData.description;
      }
    },

    updateProductSku(variant) {
      const skuElement = document.querySelector('[data-sku]');
      if (skuElement) {
        skuElement.textContent = variant.sku || '';
      }
    },

    updateProductAvailability(variant) {
      // Update inventory status if displayed
      const inventoryElement = document.querySelector('[data-product-inventory]');
      if (inventoryElement) {
        // Theme-specific inventory update logic
        if (variant.available) {
          inventoryElement.classList.remove('hide');
        } else {
          inventoryElement.classList.add('hide');
        }
      }
    },

    updateVariantData(productData, selectedVariant) {
      // Update variant JSON data for theme scripts
      const variantJsonElement = document.querySelector('[data-variant-json]');
      if (variantJsonElement) {
        variantJsonElement.textContent = JSON.stringify(productData.variants);
      }

      const currentVariantJsonElement = document.querySelector('[data-current-variant-json]');
      if (currentVariantJsonElement) {
        currentVariantJsonElement.textContent = JSON.stringify(selectedVariant);
      }
    },

    updateNoJsSelect(productData, selectedVariant) {
      // Update the no-js fallback select dropdown
      const noJsSelect = document.querySelector('select[name="id"][data-product-select]');

      if (!noJsSelect) return;

      // Clear existing options
      noJsSelect.innerHTML = '';

      // Add new options for all variants
      productData.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;

        if (variant.available) {
          option.textContent = `${variant.title} - ${this.formatMoney(variant.price)}`;
          if (variant.id === selectedVariant.id) {
            option.selected = true;
          }
        } else {
          option.disabled = true;
          option.textContent = `${variant.title} - Sold Out`;
        }

        noJsSelect.appendChild(option);
      });
    },

    reinitializeProductScripts(productData) {
      // Use custom AJAX handler instead of theme.AjaxProduct to prevent cart redirect
      const productSection = document.querySelector('[data-section-type="product"]');
      if (!productSection) return;

      const sectionId = productSection.dataset.sectionId;
      const productForm = document.querySelector(`#product-form-${sectionId}`);

      if (productForm) {
        // Remove old event listeners by cloning the form
        const newForm = productForm.cloneNode(true);
        productForm.parentNode.replaceChild(newForm, productForm);

        // Attach custom AJAX handler (similar to secondary nav)
        setTimeout(() => {
          newForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleAjaxAddToCart(newForm);
          });
        }, 150);
      }

      // Dispatch event for other theme scripts
      document.dispatchEvent(new CustomEvent('product:reinitialized', {
        detail: {
          sectionId: sectionId,
          product: productData
        }
      }));
    },

    handleAjaxAddToCart(form) {
      const addToCartBtn = form.querySelector('[data-add-to-cart]');
      const addToCartText = addToCartBtn?.querySelector('[data-add-to-cart-text]');
      const originalText = addToCartText ? addToCartText.textContent : 'Add to Cart';

      if (addToCartBtn && addToCartBtn.disabled) return;

      // Show loading state
      if (addToCartBtn) {
        addToCartBtn.classList.add('btn--loading');
        addToCartBtn.disabled = true;
      }

      const formData = new FormData(form);

      // Intercept any navigation attempts to /cart
      const preventCartRedirect = () => {
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;
        let redirectBlocked = false;

        // Override pushState
        window.history.pushState = function(state, title, url) {
          if (url && url.includes('/cart') && !redirectBlocked) {
            console.log('Blocked pushState redirect to cart');
            redirectBlocked = true;
            return;
          }
          return originalPushState.apply(this, arguments);
        };

        // Override replaceState
        window.history.replaceState = function(state, title, url) {
          if (url && url.includes('/cart') && !redirectBlocked) {
            console.log('Blocked replaceState redirect to cart');
            redirectBlocked = true;
            return;
          }
          return originalReplaceState.apply(this, arguments);
        };

        // Intercept window.location assignments
        let locationHref = window.location.href;
        Object.defineProperty(window, 'location', {
          get: () => ({
            ...window.location,
            href: locationHref,
            assign: (url) => {
              if (url.includes('/cart')) {
                console.log('Blocked location.assign to cart');
                return;
              }
              window.location.assign(url);
            }
          }),
          set: (value) => {
            if (typeof value === 'string' && value.includes('/cart')) {
              console.log('Blocked location setter to cart');
              return;
            }
            locationHref = value;
          }
        });

        // Restore after 3 seconds
        setTimeout(() => {
          window.history.pushState = originalPushState;
          window.history.replaceState = originalReplaceState;
          delete window.location;
        }, 3000);
      };

      // Activate redirect prevention
      preventCartRedirect();

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
        return response.json();
      })
      .then(data => {
        // Dispatch the ajaxProduct:added event that triggers Rebuy cart
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: data,
            addToCartBtn: addToCartBtn
          },
          bubbles: true
        }));

        // Also dispatch on document for Rebuy/cart drawer to listen
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: data
          }
        }));

        // Reset button state
        if (addToCartBtn) {
          addToCartBtn.classList.remove('btn--loading');
          addToCartBtn.disabled = false;
        }
      })
      .catch(error => {
        console.error('Error adding to cart:', error);

        // Show error state
        if (addToCartBtn) {
          addToCartBtn.classList.remove('btn--loading');
          addToCartBtn.disabled = false;
        }

        if (addToCartText) {
          addToCartText.textContent = 'Error - Try Again';
          setTimeout(() => {
            addToCartText.textContent = originalText;
          }, 2000);
        }
      });
    },

    // Utility: Format money
    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = parseInt(cents);
      }
      const moneyFormat = window.theme?.settings?.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return moneyFormat.replace('{{amount}}', value);
    },

    // Utility: Get image URL with size
    getImageUrl(src, size) {
      if (!src) return '';
      return src.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i, `_${size}.$1$2`);
    },

    // Utility: Generate srcset
    generateSrcset(src) {
      const sizes = ['300', '600', '900', '1200', '1500'];
      return sizes.map(size => {
        return `${this.getImageUrl(src, `${size}x`)} ${size}w`;
      }).join(', ');
    }
  }));
});

// Handle browser back/forward
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.productHandle) {
    // Reload page on back/forward for simplicity
    window.location.reload();
  }
});
